import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const PythonListsBasics = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Dynamic Arrays</div>
                <h1>Lists: Internals & Performance</h1>
                <p className="hero-lead">
                    The Python List is not a linked list. It is a dynamic array that over-allocates memory to achieve incredible speed.
                    Understanding its growth strategy is the key to writing scalable Python code.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In C, an array has a fixed size. You must decide upfront: "I want 10 integers."
                    In Python, <code>list</code> seems magical. You can keep appending forever.
                </p>
                <p>
                    But this magic has a cost. Under the hood, Python uses a <strong>Dynamic Array</strong> (specifically, an array of pointers to objects).
                    It tricks you into thinking it has infinite space by secretly grabbing more memory than it needs.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Structure:</strong> Why a list is actually a contiguous block of pointers.</li>
                        <li><strong>Growth Strategy:</strong> The "Over-Allocation" algorithm (0, 4, 8, 16...).</li>
                        <li><strong>Big O Analysis:</strong> Why <code>append()</code> is O(1) but <code>insert(0)</code> is O(N).</li>
                        <li><strong>Memory Layout:</strong> Visualizing how lists store references, not data.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The "Over-Allocation" Secret</h2>
                <p>
                    When you create a list <code>l = [1, 2, 3]</code>, Python doesn't just allocate space for 3 items.
                    It might allocate space for 4 or 8. This is called <strong>Over-Allocation</strong>.
                </p>
                <p>
                    This is crucial for performance. usage. If Python resized the array every single time you added an item,
                    it would be incredibly slow (copying data is expensive!). By adding extra "breathing room," Python ensures
                    that most <code>append()</code> calls just fill an empty slot instantly.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Visualizing Capacity Growth</span>
                    </div>
                    <CodeBlock language="python" code={`import sys

l = []
print(f"Size: {len(l)}, Capacity: {sys.getsizeof(l)}")

# Let's add items and watch the capacity jump
for i in range(10):
    l.append(i)
    # getsizeof returns memory in bytes
    print(f"Items: {len(l)}, Bytes: {sys.getsizeof(l)}")

# Output Logic:
# Items: 0, Bytes: 56  (Empty overhead)
# Items: 1, Bytes: 88  (Allocates room for ~4 items)
# Items: 2, Bytes: 88
# Items: 3, Bytes: 88
# Items: 4, Bytes: 88  (Full!)
# Items: 5, Bytes: 120 (Resized! Jumped to room for ~8 items)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance: Head vs Tail</h2>
                <p>
                    Because lists are <strong>contiguous arrays</strong> (blocks of memory side-by-side), shifting items is expensive.
                </p>

                <div className="analogy-box">
                    <h3> The Bookshelf Analogy</h3>
                    <p>
                        Imagine a bookshelf with books packed tightly.
                    </p>
                    <ul>
                        <li><strong>Append (Tail):</strong> Putting a book at the empty right end is easy. (O(1))</li>
                        <li><strong>Insert (Head):</strong> To put a book at the <em>start</em>, you must physically move EVERY other book one inch to the right. (O(N))</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Cost of insert(0)</span>
                    </div>
                    <CodeBlock language="python" code={`#  Bad: Treating a list like a queue
queue = []
# Each insert forces a full memory shift of all existing items
for i in range(10000):
    queue.insert(0, i)  # O(N) complexity inside a loop -&gt; O(N) total!

# Good: Appending is cheap
stack = []
for i in range(10000):
    stack.append(i)     # Amortized O(1)

# Note: If you NEED a queue (First-In-First-Out), use collections.deque!`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Memory Layout: Lists of Pointers</h2>
                <p>
                    A common misconception is that a list of integers stores the integers inside the list.
                    <strong>False.</strong> A Python list stores <strong>memory addresses</strong> (pointers) to the integer objects.
                </p>
                <div style={{ background: '#1e1e1e', padding: '20px', borderRadius: '8px', color: '#d4d4d4', fontFamily: 'monospace', margin: '20px 0' }}>
                    [List Object] <br />
                    &nbsp;&nbsp; Size: 3 <br />
                    &nbsp;&nbsp; *Items Array <br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; [Ptr] {">"} [Int Object: 10] <br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; [Ptr] {">"} [Int Object: 20] <br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; [Ptr] {">"} [Int Object: 30]
                </div>
                <p>
                    This is why Python lists can store mixed types <code>[1, "hello", 3.14]</code>.
                    The list doesn't care about the size of the data, only the size of the pointer (which is always 8 bytes on 64-bit systems).
                </p>
            </div>


            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>append()</code> and <code>pop()</code> (end of list operations).</li>
                            <li>Use <code>collections.deque</code> for queues (start of list operations).</li>
                            <li>Use List Comprehensions for creating lists (faster than loops).</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't use <code>insert(0, x)</code> in a loop.</li>
                            <li>Don't allow lists to grow infinitely (memory leaks).</li>
                            <li>Don't assume <code>x in list</code> is fast (it scans the whole list).</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonListsBasics;



